import * as React from "react";
import { CSS, styled } from "utils";
import type { InferGetStaticPropsType, NextPage } from "next";
import { useRouter } from "next/router";
import Image from "next/image";
import useInView from "react-cool-inview";
import { Paths } from "config/paths";
import {
  Page,
  Box,
  SearchCard,
  Heading,
  Flex,
  Paragraph,
  Navigation,
  Main,
  MainPropertiesHeader,
  MainPropertiesList,
  mainFullbleedStyle,
  Button,
  ButtonLink,
  Footer,
  Video,
  IconPromoCheck,
  IconPromoSecure,
} from "components";
import * as data from "data";

const StyledHero = styled(Box, mainFullbleedStyle, {
  position: "relative",
  height: 426,
  padding: "$6",
  paddingTop: "$7",
  textAlign: "center",
  color: "$white",
  "@bp2": { height: 335, paddingX: "$8", paddingTop: "$9" },
});

const StyledHeroImage = styled(Image, {
  position: "relative",
  zIndex: -1,
});

const StyledVideoContainer = styled(Box, mainFullbleedStyle, {
  maxHeight: 514,
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const StyledSearchCard = styled(SearchCard, {
  backgroundColor: "$white",
  marginX: "auto",
  marginTop: -170,
  marginBottom: "$7",
  maxWidth: 857,
  padding: "$5",
  "@bp2": { marginTop: -97 },
});

const articleBaseStyles: CSS = {
  padding: "$4",
  backgroundColor: "$grey900",
  color: "$white",
};

const StyledWhyUsContainer = styled(Box, articleBaseStyles, {
  borderRadius: "$2",
  marginBottom: "$11",
  paddingTop: "$5",
  paddingBottom: "$7",
});

const StyledWhyUsHeading = styled(Heading, {
  marginBottom: "$4",
  lineHeight: 1,
  "@bp2": {
    marginBottom: "$5",
  },
});

const StyledWhyUsImage = styled(Box, {
  marginBottom: "$4",
});

const StyledContactContainer = styled(
  Flex,
  articleBaseStyles,
  mainFullbleedStyle,
  {
    color: "$white",
    flexDirection: "column",
    padding: "$4",
    "@bp2": { flexDirection: "row", padding: "$9" },
    "@bp3": { paddingX: "$12" },
  }
);

const StyledContactParagraph = styled(Paragraph, {
  fontSize: "$7",
  maxWidth: 558,
  marginBottom: "$4",
  "@bp2": { marginBottom: 0, marginRight: "$5" },
});

type Props = InferGetStaticPropsType<typeof getStaticProps>;

const Home: NextPage<Props> = ({ properties }) => {
  const router = useRouter();

  const handleSearch = (searchTerm: string) => {
    router.push({
      pathname: Paths.properties,
      query: { searchTerm },
    });
  };

  const { observe, inView } = useInView({
    unobserveOnEnter: true,
    rootMargin: "500px",
  });
  return (
    <Page>
      <Navigation variation="home" />
      <Main>
        <StyledHero as="article">
          <StyledHeroImage
            layout="fill"
            objectFit="cover"
            quality={100}
            src="/images/hero.jpg"
            alt=""
            priority
          />
          <Heading
            as="h1"
            css={{
              marginX: "auto",
              maxWidth: 256,
              "@bp2": { maxWidth: "100%" },
            }}
          >
            Bienvenidos a Campos Uy
          </Heading>
          <Paragraph variant="subtitle">
            Compra-venta de campos en Uruguay
          </Paragraph>
        </StyledHero>
        <Box as="article">
          <StyledSearchCard onSearch={handleSearch} />
        </Box>
        <Box as="article" css={{ marginBottom: "$11" }}>
          <MainPropertiesHeader>Propiedades destacadas</MainPropertiesHeader>
          <MainPropertiesList properties={properties} />
        </Box>

        <StyledWhyUsContainer as="article">
          <Paragraph css={{ marginBottom: "$2" }}>
            ¿Por que elegirnos?
          </Paragraph>
          <Heading
            css={{
              fontSize: "$5",
              marginBottom: "$4",
              "@bp2": { fontSize: "$6", marginBottom: "$7" },
            }}
          >
            Algunas razones para elegirnos
          </Heading>
          <Flex
            css={{
              flexDirection: "column",
              gap: "$5",
              "@bp2": { flexDirection: "row" },
            }}
          >
            <Box>
              <StyledWhyUsImage>
                <IconPromoSecure />
              </StyledWhyUsImage>
              <StyledWhyUsHeading as="h3">
                Seguridad y confianza
              </StyledWhyUsHeading>
              <Paragraph>
                Bridamos asesoramiento personalizado con nuestro equipo de
                experto en la compra, venta, arrendamiento de campos.
              </Paragraph>
            </Box>
            <Box>
              <StyledWhyUsImage>
                <IconPromoCheck />
              </StyledWhyUsImage>
              <StyledWhyUsHeading as="h3">
                Constante asesoramiento
              </StyledWhyUsHeading>
              <Paragraph>
                Nuestro equipo de profesionales trabaja de manera
                multidisiplinaria para darle el mejor servicio de asesoramiento
                a nuestros clientes.
              </Paragraph>
            </Box>
            <Box>
              <StyledWhyUsImage>
                <IconPromoCheck />
              </StyledWhyUsImage>
              <StyledWhyUsHeading as="h3">
                Excelente reputación
              </StyledWhyUsHeading>
              <Paragraph>
                Contamos con un equipo especializado en el área contable y
                finanzas. Asesoramos, administramos y gestionamos tu nueva
                inversión.
              </Paragraph>
            </Box>
          </Flex>
        </StyledWhyUsContainer>

        <StyledVideoContainer as="article" ref={observe}>
          {inView && (
            <Video autoPlay loop muted src="/videos/campos_uruguay.mp4" />
          )}
        </StyledVideoContainer>
        <StyledContactContainer
          as="article"
          align="center"
          justify="between"
          css={{ marginBottom: 0 }}
        >
          <StyledContactParagraph>
            Buscando comprar una nueva propiedad o ¿Vender una existente?
          </StyledContactParagraph>
          <ButtonLink href={Paths.contact}>
            <Button css={{ maxWidth: 330 }}>Contáctanos</Button>
          </ButtonLink>
        </StyledContactContainer>
      </Main>

      <Footer />
    </Page>
  );
};

export const getStaticProps = async () => {
  return { props: { properties: data.properties } };
};

export default Home;
